import { STAFF_BULK_UPLOAD_FIELDS } from '@/src/constants/data'
import { ActionRow, SmButton, StyledSelect } from '@/src/pages/Pharmacist/PharmacyWorkspace/panels/shared'
import {
  autoMapHeaders,
  escapeCsvCell,
  parseCsv,
  type ColumnMap,
  type ParsedCsv
} from '@/src/pages/Pharmacist/PharmacyWorkspace/utils/csv'
import { useBulkUpload, useDepartment, useFacility, useRoles } from '@hook'
import { Button, Modal } from '@ui'
import { getError, showToast } from '@util'
import { useEffect, useMemo, useRef, useState } from 'react'

const dropdownFields = new Set(['department', 'role', 'facility'])
const mapableFields = STAFF_BULK_UPLOAD_FIELDS.filter(field => !dropdownFields.has(field.name))
const requiredMapFields = ['first_name', 'last_name', 'email'] as const
const apiHeaders = [
  'first_name',
  'last_name',
  'middle_name',
  'email',
  'phone_number',
  'gender',
  'date_of_birth',
  'department',
  'specialty',
  'role',
  'facility'
] as const

type Props = {
  onClose: () => void
  isLoading?: boolean
}

type Step = 'upload' | 'map'

const buildCanonicalCsv = ({
  headers,
  rows,
  columnMap,
  department,
  role,
  facility
}: {
  headers: string[]
  rows: string[][]
  columnMap: ColumnMap
  department: string
  role: string
  facility: string
}) => {
  const headerIndex = new Map(headers.map((h, i) => [h, i]))

  const lines = [
    apiHeaders.join(','),
    ...rows.map(row => {
      const get = (name: string) => {
        const srcHeader = columnMap[name]
        if (!srcHeader) return ''
        const idx = headerIndex.get(srcHeader)
        if (idx === undefined) return ''
        return String(row[idx] ?? '').trim()
      }

      const values: Record<string, string> = {
        first_name: get('first_name'),
        last_name: get('last_name'),
        middle_name: get('middle_name'),
        email: get('email'),
        phone_number: get('phone_number'),
        gender: get('gender'),
        date_of_birth: get('date_of_birth'),
        department,
        specialty: get('specialty'),
        role,
        facility
      }

      return apiHeaders.map(h => escapeCsvCell(values[h] ?? '')).join(',')
    })
  ]

  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' })
  return new File([blob], 'staff-bulk-mapped.csv', { type: 'text/csv' })
}

const StaffBulkMapModal = ({ onClose, isLoading }: Props) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { bulkImport, isLoading: importLoading } = useBulkUpload({
    context: 'staffs',
    invalidateQueryKey: ['staffs']
  })
  const { departments } = useDepartment({ page: 1, limit: 200 })
  const { roles } = useRoles({ page: 1, pageLimit: 200 })
  const { facilities } = useFacility({ page: 1, limit: 200 })

  const [step, setStep] = useState<Step>('upload')
  const [fileName, setFileName] = useState('')
  const [parsed, setParsed] = useState<ParsedCsv | null>(null)
  const [columnMap, setColumnMap] = useState<ColumnMap>({})
  const [department, setDepartment] = useState('')
  const [role, setRole] = useState('')
  const [facility, setFacility] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const departmentOptions = useMemo(
    () => [
      { label: 'Select department', value: '' },
      ...(departments || []).map(item => ({
        label: item.attributes?.name ?? '',
        value: item.attributes?.name ?? ''
      }))
    ],
    [departments]
  )

  const roleOptions = useMemo(
    () => [
      { label: 'Select role', value: '' },
      ...(roles || []).map(item => ({
        label: item.attributes?.display_name || item.attributes?.name || '',
        value: item.attributes?.name ?? ''
      }))
    ],
    [roles]
  )

  const facilityOptions = useMemo(
    () => [
      { label: 'Select facility', value: '' },
      ...(facilities || []).map(item => ({
        label: item.attributes?.name ?? '',
        value: item.attributes?.name ?? ''
      }))
    ],
    [facilities]
  )

  const csvHeaderOptions = useMemo(() => {
    if (!parsed) return []
    return [{ label: '— Ignore —', value: '' }, ...parsed.headers.map(h => ({ label: h, value: h }))]
  }, [parsed])

  const canImport =
    Boolean(parsed?.rows.length) &&
    requiredMapFields.every(field => Boolean(columnMap[field])) &&
    Boolean(department) &&
    Boolean(role) &&
    Boolean(facility)

  useEffect(() => {
    if (parsed) {
      setColumnMap(autoMapHeaders(parsed.headers, mapableFields))
    }
  }, [parsed])

  const handleFile = async (file: File) => {
    if (!file.name.toLowerCase().endsWith('.csv')) {
      showToast('Please select a CSV file')
      return
    }

    try {
      const text = await file.text()
      const next = parseCsv(text)
      if (!next.headers.length) {
        showToast('CSV has no header row')
        return
      }
      if (!next.rows.length) {
        showToast('CSV has no data rows')
        return
      }

      setFileName(file.name)
      setParsed(next)
      setStep('map')
    } catch {
      showToast('Could not read CSV file')
    }
  }

  const resetUpload = () => {
    setStep('upload')
    setParsed(null)
    setColumnMap({})
    setDepartment('')
    setRole('')
    setFacility('')
    setFileName('')
  }

  const handleImport = async () => {
    if (!parsed || !canImport) return

    setSubmitting(true)
    try {
      const file = buildCanonicalCsv({
        headers: parsed.headers,
        rows: parsed.rows,
        columnMap,
        department,
        role,
        facility
      })
      const res = await bulkImport(file)
      const created = res?.meta?.created_count ?? 0
      const failed = res?.meta?.failed_rows?.length ?? 0
      showToast(`Staff upload complete: ${created} created, ${failed} failed`)
      onClose()
    } catch (error) {
      getError(error)
      throw error
    } finally {
      setSubmitting(false)
    }
  }

  const busy = Boolean(isLoading || importLoading || submitting)

  return (
    <Modal onClose={onClose} title="Bulk staff upload">
      <div className="flex max-h-[min(70vh,640px)] flex-col gap-4 overflow-y-auto p-6">
        {step === 'upload' ? (
          <>
            <p className="m-0 text-sm text-[#5C6C71]">
              Upload a CSV, map each column to the staff fields, then choose department, role, and facility.
            </p>
            <input
              accept=".csv,text/csv"
              className="hidden"
              onChange={e => {
                const file = e.target.files?.[0]
                e.target.value = ''
                if (file) void handleFile(file)
              }}
              ref={fileInputRef}
              type="file"
            />
            <ActionRow>
              <SmButton kinds="secondary" label="Select CSV file" onClick={() => fileInputRef.current?.click()} />
              <Button kinds="outline" label="Cancel" onClick={onClose} size="wide" />
            </ActionRow>
          </>
        ) : (
          <>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="m-0 text-sm text-[#5C6C71]">
                <span className="font-medium text-[#1A1A1A]">{fileName}</span>
                {parsed ? ` · ${parsed.rows.length} row${parsed.rows.length === 1 ? '' : 's'}` : ''}
              </p>
              <SmButton disabled={busy} kinds="outline" label="Choose another file" onClick={resetUpload} />
            </div>

            <div>
              <p className="mb-2 mt-0 text-sm font-medium text-[#1A1A1A]">Map CSV columns</p>
              <div className="flex max-h-[min(280px,35vh)] flex-col gap-2 overflow-y-auto rounded-lg border border-[#E6E6E6] p-3">
                {mapableFields.map(field => {
                  const required = requiredMapFields.includes(field.name as (typeof requiredMapFields)[number])
                  return (
                    <div
                      className="grid grid-cols-1 items-center gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]"
                      key={field.name}
                    >
                      <span className="text-sm text-[#1A1A1A]">
                        {field.label}
                        {required ? ' *' : ''}
                      </span>
                      <StyledSelect
                        disabled={busy}
                        onChange={({ value }) => setColumnMap(prev => ({ ...prev, [field.name]: value }))}
                        options={csvHeaderOptions}
                        value={columnMap[field.name] ?? ''}
                      />
                    </div>
                  )
                })}
              </div>
            </div>

            <div>
              <p className="mb-2 mt-0 text-sm font-medium text-[#1A1A1A]">Apply to all rows</p>
              <div className="flex flex-col gap-2 rounded-lg border border-[#E6E6E6] p-3">
                <div className="grid grid-cols-1 items-center gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
                  <span className="text-sm text-[#1A1A1A]">Department *</span>
                  <StyledSelect
                    disabled={busy}
                    onChange={({ value }) => setDepartment(value)}
                    options={departmentOptions}
                    value={department}
                  />
                </div>
                <div className="grid grid-cols-1 items-center gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
                  <span className="text-sm text-[#1A1A1A]">Role *</span>
                  <StyledSelect
                    disabled={busy}
                    onChange={({ value }) => setRole(value)}
                    options={roleOptions}
                    value={role}
                  />
                </div>
                <div className="grid grid-cols-1 items-center gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
                  <span className="text-sm text-[#1A1A1A]">Facility *</span>
                  <StyledSelect
                    disabled={busy}
                    onChange={({ value }) => setFacility(value)}
                    options={facilityOptions}
                    value={facility}
                  />
                </div>
              </div>
            </div>

            <ActionRow>
              <SmButton
                disabled={!canImport || busy}
                kinds="primary"
                label={busy ? 'Importing…' : 'Import'}
                onClick={() => void handleImport()}
              />
              <Button disabled={busy} kinds="secondary" label="Cancel" onClick={onClose} size="wide" />
            </ActionRow>
          </>
        )}
      </div>
    </Modal>
  )
}

export default StaffBulkMapModal
